import React, { useState, useCallback, useRef } from 'react';
import { HybridIconGenerationService } from '@/services/GeminiIconGenerationService';
import { KNOWN_SUPABASE_ICONS, getIconUrl, IconFile } from '@/utils/SupabaseIconWorkaround';

interface AIIconGeneratorProps {
  onIconGenerated?: (filename: string, publicUrl: string) => void;
  onError?: (error: string) => void;
}

interface GenerationJob {
  id: string;
  iconName: string;
  status: 'pending' | 'generating' | 'uploading' | 'completed' | 'failed';
  progress: number;
  filename?: string;
  publicUrl?: string;
  error?: string;
  preview?: string;
}

export const AIIconGenerator: React.FC<AIIconGeneratorProps> = ({
  onIconGenerated,
  onError
}) => {
  const [service] = useState(() => new HybridIconGenerationService());
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Smart icon suggestions based on existing icons and missing needs
  const getSmartSuggestions = useCallback(() => {
    const existing = KNOWN_SUPABASE_ICONS.map(icon => icon.name.toLowerCase());
    
    const missingIcons = [
      {
        iconName: 'atlas-avatar.png',
        category: 'avatar' as const,
        description: 'Atlas AI Strategy Advisor avatar - masculine, wise, strategic thinking elements',
        characterContext: 'atlas',
        priority: 'high'
      },
      {
        iconName: 'maya-avatar.png', 
        category: 'avatar' as const,
        description: 'Maya AI Communication Expert avatar - feminine, articulate, communication elements',
        characterContext: 'maya',
        priority: 'high'
      },
      {
        iconName: 'workflow-process.png',
        category: 'ui' as const,
        description: 'Workflow and process management icon with connected flowchart elements',
        characterContext: 'ui',
        priority: 'medium'
      },
      {
        iconName: 'network-connection.png',
        category: 'ui' as const,
        description: 'Network connection icon with connected nodes or mesh pattern',
        characterContext: 'ui',
        priority: 'medium'
      },
      {
        iconName: 'settings-gear.png',
        category: 'ui' as const,
        description: 'Settings configuration icon with modern gear design',
        characterContext: 'ui',
        priority: 'low'
      },
      {
        iconName: 'notification-bell.png',
        category: 'ui' as const,
        description: 'Notification alert icon with clean bell design',
        characterContext: 'ui',
        priority: 'low'
      },
      {
        iconName: 'calendar-schedule.png',
        category: 'ui' as const,
        description: 'Calendar scheduling icon showing dates and time management',
        characterContext: 'ui',
        priority: 'medium'
      },
      {
        iconName: 'document-file.png',
        category: 'ui' as const,
        description: 'Document file icon representing text documents and files',
        characterContext: 'ui',
        priority: 'medium'
      },
      {
        iconName: 'search-magnify.png',
        category: 'ui' as const,
        description: 'Search and discovery icon with magnifying glass',
        characterContext: 'ui',
        priority: 'medium'
      },
      {
        iconName: 'bookmark-save.png',
        category: 'ui' as const,
        description: 'Bookmark and save icon for content curation',
        characterContext: 'ui',
        priority: 'low'
      }
    ].filter(suggestion => !existing.some(existingName => 
      existingName.includes(suggestion.iconName.toLowerCase().replace('.png', ''))
    ));

    return missingIcons.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }, []);

  // Generate single icon with reference image support
  const generateSingleIcon = useCallback(async (request: {
    iconName: string;
    category: 'avatar' | 'badge' | 'ui' | 'state' | 'functional' | 'animation';
    description: string;
    characterContext?: string;
    referenceImages?: File[];
  }) => {
    const jobId = Date.now().toString();
    const job: GenerationJob = {
      id: jobId,
      iconName: request.iconName,
      status: 'pending',
      progress: 0
    };

    setJobs(prev => [...prev, job]);

    try {
      // Update status to generating
      setJobs(prev => prev.map(j => 
        j.id === jobId ? { ...j, status: 'generating', progress: 20 } : j
      ));

      console.log('🎨 [AI-GENERATOR] Starting generation for:', request.iconName);

      // Convert reference images to base64 if provided
      let referenceBase64: string[] | undefined;
      if (request.referenceImages && request.referenceImages.length > 0) {
        referenceBase64 = await Promise.all(
          request.referenceImages.map(file => new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1]); // Remove data:image/png;base64, prefix
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }))
        );
        console.log('📎 [AI-GENERATOR] Using', referenceBase64.length, 'reference images for character consistency');
      }

      // Generate icon using Gemini 2.5 Flash Image with reference images
      const result = await service.generateAndUploadIcon({
        ...request,
        referenceImages: referenceBase64
      });

      // Update with preview
      setJobs(prev => prev.map(j => 
        j.id === jobId ? { 
          ...j, 
          status: 'uploading', 
          progress: 60,
          preview: result.icon.url,
          filename: result.icon.filename
        } : j
      ));

      // Complete
      setJobs(prev => prev.map(j => 
        j.id === jobId ? { 
          ...j, 
          status: 'completed', 
          progress: 100,
          publicUrl: result.publicUrl
        } : j
      ));

      console.log('✅ [AI-GENERATOR] Icon generated successfully:', result.publicUrl);

      if (onIconGenerated) {
        onIconGenerated(result.icon.filename, result.publicUrl);
      }

      return result;

    } catch (error) {
      console.error('❌ [AI-GENERATOR] Generation failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      setJobs(prev => prev.map(j => 
        j.id === jobId ? { 
          ...j, 
          status: 'failed', 
          progress: 0,
          error: errorMessage
        } : j
      ));

      if (onError) {
        onError(errorMessage);
      }

      throw error;
    }
  }, [service, onIconGenerated, onError]);

  // Generate all missing icons
  const generateAllMissing = useCallback(async () => {
    const suggestions = getSmartSuggestions();
    console.log('🚀 [AI-GENERATOR] Starting batch generation:', suggestions.length, 'icons');

    for (const suggestion of suggestions) {
      try {
        await generateSingleIcon(suggestion);
        // Small delay between generations
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error('❌ [AI-GENERATOR] Failed to generate:', suggestion.iconName, error);
        // Continue with next icon
      }
    }
  }, [generateSingleIcon, getSmartSuggestions]);

  // Analyze existing icons for character consistency
  const analyzeExistingIcons = useCallback(async () => {
    setIsAnalyzing(true);
    
    try {
      console.log('🔍 [AI-GENERATOR] Analyzing existing icons for consistency...');
      
      // This could use computer vision to analyze existing icons
      // For now, we'll provide a manual analysis based on known patterns
      
      const analysis = {
        characterConsistency: 'Good',
        colorScheme: 'Blue gradient system maintained',
        style: 'Professional, clean, accessible',
        recommendations: [
          'Generate missing avatar characters (Maya, Atlas)',
          'Create workflow and process icons',
          'Add notification and calendar icons',
          'Maintain consistent blue gradient scheme'
        ]
      };

      console.log('✅ [AI-GENERATOR] Analysis complete:', analysis);
      return analysis;

    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  // Handle reference image upload
  const handleReferenceUpload = useCallback((callback: (files: File[]) => void) => {
    if (fileInputRef.current) {
      const input = fileInputRef.current;
      input.onchange = (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        callback(files);
      };
      input.click();
    }
  }, []);

  const suggestions = getSmartSuggestions();
  const activeJobs = jobs.filter(j => j.status !== 'completed' && j.status !== 'failed');
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const failedJobs = jobs.filter(j => j.status === 'failed');

  return (
    <div className="space-y-6">
      {/* Hidden file input for reference images */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
      />

      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
        <h2 className="text-xl font-semibold text-blue-900 mb-2">
          🤖 AI Icon Generator
        </h2>
        <p className="text-blue-700 mb-4">
          Powered by <strong>Gemini 2.5 Flash Image</strong> (Google API + Fal.ai fallback) with character consistency and automated Supabase upload
        </p>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={analyzeExistingIcons}
            disabled={isAnalyzing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex items-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Analyzing...
              </>
            ) : (
              <>🔍 Analyze Existing</>
            )}
          </button>

          <button
            onClick={generateAllMissing}
            disabled={activeJobs.length > 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400"
          >
            🚀 Generate All Missing ({suggestions.length})
          </button>
        </div>
      </div>

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4">
            💡 Smart Icon Suggestions ({suggestions.length})
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.slice(0, 6).map((suggestion) => (
              <div key={suggestion.iconName} className="p-4 bg-white rounded-lg border border-yellow-300">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      {suggestion.iconName}
                    </h4>
                    <div className="flex gap-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        suggestion.category === 'avatar' ? 'bg-purple-100 text-purple-700' :
                        suggestion.category === 'ui' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {suggestion.category}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        suggestion.priority === 'high' ? 'bg-red-100 text-red-700' :
                        suggestion.priority === 'medium' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {suggestion.priority}
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">
                  {suggestion.description}
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => generateSingleIcon(suggestion)}
                    disabled={activeJobs.some(j => j.iconName === suggestion.iconName)}
                    className="text-xs bg-yellow-600 text-white py-1 px-3 rounded hover:bg-yellow-700 disabled:bg-yellow-400"
                  >
                    Generate
                  </button>
                  
                  <button
                    onClick={() => handleReferenceUpload((files) => 
                      generateSingleIcon({ ...suggestion, referenceImages: files })
                    )}
                    className="text-xs bg-gray-500 text-white py-1 px-3 rounded hover:bg-gray-600"
                  >
                    + Reference
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {suggestions.length > 6 && (
            <p className="text-yellow-700 text-sm mt-4">
              And {suggestions.length - 6} more suggestions...
            </p>
          )}
        </div>
      )}

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">
            🎨 Generating Icons ({activeJobs.length})
          </h3>
          
          <div className="space-y-4">
            {activeJobs.map((job) => (
              <div key={job.id} className="p-4 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">{job.iconName}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    job.status === 'generating' ? 'bg-blue-100 text-blue-700' :
                    job.status === 'uploading' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {job.status}
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${job.progress}%` }}
                  ></div>
                </div>
                
                <p className="text-sm text-gray-600">
                  {job.status === 'generating' && 'Creating with Gemini 2.5 Flash Image...'}
                  {job.status === 'uploading' && 'Uploading to Supabase storage...'}
                  {job.status === 'pending' && 'Queued for generation...'}
                </p>
                
                {job.preview && (
                  <div className="mt-3">
                    <img 
                      src={job.preview} 
                      alt="Generated icon preview" 
                      className="w-16 h-16 rounded-lg border object-cover"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Jobs */}
      {completedJobs.length > 0 && (
        <div className="p-6 bg-green-50 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-4">
            ✅ Generated Icons ({completedJobs.length})
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {completedJobs.map((job) => (
              <div key={job.id} className="p-3 bg-white rounded-lg border text-center">
                {job.preview && (
                  <img 
                    src={job.preview} 
                    alt={job.iconName} 
                    className="w-16 h-16 mx-auto mb-2 rounded-lg object-cover"
                  />
                )}
                <p className="text-xs font-medium text-gray-800 truncate">
                  {job.iconName}
                </p>
                {job.publicUrl && (
                  <a 
                    href={job.publicUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-green-600 hover:text-green-800"
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failed Jobs */}
      {failedJobs.length > 0 && (
        <div className="p-6 bg-red-50 rounded-lg border border-red-200">
          <h3 className="text-lg font-semibold text-red-800 mb-4">
            ❌ Failed Icons ({failedJobs.length})
          </h3>
          
          <div className="space-y-3">
            {failedJobs.map((job) => (
              <div key={job.id} className="p-3 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-gray-800 text-sm">{job.iconName}</h4>
                  <button
                    onClick={() => generateSingleIcon({
                      iconName: job.iconName,
                      category: 'ui',
                      description: `Retry generation for ${job.iconName}`
                    })}
                    className="text-xs bg-red-600 text-white py-1 px-2 rounded hover:bg-red-700"
                  >
                    Retry
                  </button>
                </div>
                {job.error && (
                  <p className="text-xs text-red-600">{job.error}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage Stats */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-2">💰 Usage Statistics</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Generated:</span>
            <span className="ml-1 font-semibold">{completedJobs.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Cost:</span>
            <span className="ml-1 font-semibold">${(completedJobs.length * 0.039).toFixed(3)}</span>
          </div>
          <div>
            <span className="text-gray-600">In Progress:</span>
            <span className="ml-1 font-semibold">{activeJobs.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Failed:</span>
            <span className="ml-1 font-semibold">{failedJobs.length}</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Powered by Gemini 2.5 Flash Image • $0.039 per icon
        </p>
      </div>
    </div>
  );
};

export default AIIconGenerator;